// ═══════════════════════════════════════════════════════════════════════════════
// KNOWLEDGE BASE PAGE - Configuration de la base de connaissances de l'assistant
// Dual mode: Formulaire manuel + Wizard conversationnel
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain, MessageSquare, Settings, FileText, Tags, Plus, Trash2,
  ChevronRight, ChevronLeft, Check, Sparkles, Save, RefreshCw,
  User, Building2, MapPin, Clock, Ban, Heart, Mail, Pencil,
  BookOpen, Zap, Send, Loader2, X
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER - Get user info
// ═══════════════════════════════════════════════════════════════════════════════
const getUserId = () => {
  const ecosystemUser = localStorage.getItem('ecosystem_user');
  const clientUser = localStorage.getItem('client_user');
  if (ecosystemUser) {
    try { return JSON.parse(ecosystemUser).id; } catch(e) {}
  }
  if (clientUser) {
    try { return JSON.parse(clientUser).id; } catch(e) {}
  }
  return null;
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

export default function KnowledgeBasePage() {
  const navigate = useNavigate();
  const userId = getUserId();
  
  // State
  const [activeTab, setActiveTab] = useState('wizard'); // 'wizard' | 'manual'
  const [knowledgeBase, setKnowledgeBase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Load knowledge base
  useEffect(() => {
    if (userId) {
      fetchKnowledgeBase();
    } else {
      setLoading(false);
    }
  }, [userId]);
  
  const fetchKnowledgeBase = async () => {
    try {
      const response = await fetch(`${API_URL}/api/knowledge-base/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setKnowledgeBase(data);
        // Si le wizard est déjà complété, passer en mode manuel
        if (data.wizard_completed) {
          setActiveTab('manual');
        }
      }
    } catch (error) {
      console.error('Error fetching knowledge base:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };
  
  const saveKnowledgeBase = async (updates) => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/knowledge-base/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      if (response.ok) {
        setKnowledgeBase(prev => ({ ...prev, ...updates }));
        toast.success('Sauvegardé !');
        return true;
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
    return false;
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }
  
  if (!userId) {
    return (
      <div className="max-w-4xl mx-auto p-6 text-center">
        <Brain className="w-16 h-16 mx-auto mb-4 text-gray-300" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Connexion requise</h2>
        <p className="text-gray-500 mb-4">Connecte-toi pour configurer ta base de connaissances</p>
        <Button onClick={() => navigate('/auth')} className="bg-[#1E3A5F]">
          Se connecter
        </Button>
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] rounded-xl">
            <Brain className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Ma Base de Connaissances</h1>
            <p className="text-sm text-gray-500">Apprends à ton assistant à te connaître</p>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-xl">
        <button
          onClick={() => setActiveTab('wizard')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'wizard'
              ? 'bg-white text-[#1E3A5F] shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Sparkles className="w-4 h-4" />
          Mode Assistant
        </button>
        <button
          onClick={() => setActiveTab('manual')}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
            activeTab === 'manual'
              ? 'bg-white text-[#1E3A5F] shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Settings className="w-4 h-4" />
          Mode Manuel
        </button>
      </div>
      
      {/* Content */}
      <AnimatePresence mode="wait">
        {activeTab === 'wizard' ? (
          <motion.div
            key="wizard"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <WizardMode 
              userId={userId} 
              knowledgeBase={knowledgeBase}
              onComplete={(data) => {
                setKnowledgeBase(data);
                setActiveTab('manual');
              }}
            />
          </motion.div>
        ) : (
          <motion.div
            key="manual"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <ManualMode 
              knowledgeBase={knowledgeBase}
              onSave={saveKnowledgeBase}
              saving={saving}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// WIZARD MODE - Configuration conversationnelle
// ═══════════════════════════════════════════════════════════════════════════════

function WizardMode({ userId, knowledgeBase, onComplete }) {
  const [questions, setQuestions] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const messagesEndRef = useRef(null);
  const assistantName = localStorage.getItem('assistant_custom_name') || 'Mon Assistant';
  
  // Charger les questions
  useEffect(() => {
    fetchQuestions();
  }, [userId]);
  
  const fetchQuestions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/knowledge-base/${userId}/wizard-questions`);
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions);
        
        // Ajouter le premier message
        if (data.questions.length > 0) {
          setMessages([{
            role: 'assistant',
            content: data.questions[0].question,
            questionData: data.questions[0]
          }]);
        }
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);
  
  const handleAnswer = async (answer, questionData) => {
    // Ajouter la réponse utilisateur
    setMessages(prev => [...prev, { role: 'user', content: answer }]);
    
    // Sauvegarder la réponse
    const newAnswers = { ...answers };
    if (questionData.field) {
      // Gérer les champs imbriqués (ex: communication_style.default_tone)
      const fieldParts = questionData.field.split('.');
      if (fieldParts.length === 2) {
        if (!newAnswers[fieldParts[0]]) newAnswers[fieldParts[0]] = {};
        newAnswers[fieldParts[0]][fieldParts[1]] = answer;
      } else {
        newAnswers[questionData.field] = answer;
      }
    }
    setAnswers(newAnswers);
    
    // Passer à la question suivante
    const nextStep = currentStep + 1;
    
    if (nextStep < questions.length) {
      setCurrentStep(nextStep);
      const nextQuestion = questions[nextStep];
      
      // Petit délai pour l'effet conversation
      setTimeout(() => {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: nextQuestion.question,
          questionData: nextQuestion
        }]);
      }, 500);
    } else {
      // Wizard terminé - sauvegarder
      setSaving(true);
      try {
        const finalAnswers = { ...newAnswers, wizard_completed: true };
        const response = await fetch(`${API_URL}/api/knowledge-base/${userId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(finalAnswers)
        });
        
        if (response.ok) {
          toast.success('Configuration terminée !');
          onComplete({ ...knowledgeBase, ...finalAnswers });
        }
      } catch (error) {
        console.error('Error saving:', error);
        toast.error('Erreur lors de la sauvegarde');
      } finally {
        setSaving(false);
      }
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    
    const currentQuestion = questions[currentStep];
    handleAnswer(inputValue.trim(), currentQuestion);
    setInputValue('');
  };
  
  const currentQuestion = questions[currentStep];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }
  
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Chat header */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] px-4 py-3 flex items-center gap-3">
        <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="font-semibold text-white">{assistantName}</h3>
          <p className="text-xs text-white/70">Configuration guidée</p>
        </div>
        <div className="ml-auto text-xs text-white/60">
          Étape {currentStep + 1} / {questions.length}
        </div>
      </div>
      
      {/* Messages */}
      <div className="h-[400px] overflow-y-auto p-4 space-y-4 bg-gray-50">
        {messages.map((msg, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${
              msg.role === 'user'
                ? 'bg-[#1E3A5F] text-white rounded-br-md'
                : 'bg-white border border-gray-200 text-gray-800 rounded-bl-md'
            }`}>
              {msg.content}
              
              {/* Options de choix */}
              {msg.role === 'assistant' && msg.questionData?.type === 'choice' && idx === messages.length - 1 && (
                <div className="mt-3 space-y-2">
                  {msg.questionData.options.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => handleAnswer(opt.value, msg.questionData)}
                      className="w-full text-left px-3 py-2 bg-gray-50 hover:bg-[#1E3A5F]/10 rounded-lg text-sm transition-colors border border-gray-200"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Confirmation */}
              {msg.role === 'assistant' && msg.questionData?.type === 'confirm' && idx === messages.length - 1 && (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => handleAnswer('oui', msg.questionData)}
                    className="px-4 py-2 bg-[#1E3A5F] text-white rounded-lg text-sm font-medium hover:bg-[#2d5a8a] transition-colors"
                  >
                    C'est parti !
                  </button>
                </div>
              )}
              
              {/* Tags input */}
              {msg.role === 'assistant' && msg.questionData?.type === 'tags' && idx === messages.length - 1 && (
                <TagsInput
                  placeholder={msg.questionData.placeholder}
                  onSubmit={(tags) => handleAnswer(tags, msg.questionData)}
                />
              )}
              
              {/* Multi-choice */}
              {msg.role === 'assistant' && msg.questionData?.type === 'multi_choice' && idx === messages.length - 1 && (
                <MultiChoiceInput
                  options={msg.questionData.options}
                  onSubmit={(values) => handleAnswer(values, msg.questionData)}
                />
              )}
            </div>
          </motion.div>
        ))}
        
        {saving && (
          <div className="flex justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-[#1E3A5F]" />
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input (pour text/textarea) */}
      {currentQuestion && ['text', 'textarea'].includes(currentQuestion.type) && (
        <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 bg-white">
          <div className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={currentQuestion.placeholder || 'Ta réponse...'}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F]"
              autoFocus
            />
            <button
              type="submit"
              disabled={!inputValue.trim()}
              className="px-4 py-2.5 bg-[#1E3A5F] text-white rounded-xl hover:bg-[#2d5a8a] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAGS INPUT COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function TagsInput({ placeholder, onSubmit }) {
  const [tags, setTags] = useState([]);
  const [inputValue, setInputValue] = useState('');
  
  const addTag = () => {
    if (inputValue.trim() && !tags.includes(inputValue.trim())) {
      setTags([...tags, inputValue.trim()]);
      setInputValue('');
    }
  };
  
  const removeTag = (tag) => {
    setTags(tags.filter(t => t !== tag));
  };
  
  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap gap-1">
        {tags.map(tag => (
          <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-[#1E3A5F]/10 text-[#1E3A5F] rounded-full text-xs">
            {tag}
            <button onClick={() => removeTag(tag)} className="hover:text-red-500">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
          placeholder={placeholder}
          className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1E3A5F]"
        />
        <button
          type="button"
          onClick={addTag}
          className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>
      <button
        type="button"
        onClick={() => onSubmit(tags)}
        className="w-full py-2 bg-[#1E3A5F] text-white rounded-lg text-sm font-medium hover:bg-[#2d5a8a]"
      >
        {tags.length > 0 ? 'Valider' : 'Passer'}
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MULTI-CHOICE INPUT COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

function MultiChoiceInput({ options, onSubmit }) {
  const [selected, setSelected] = useState([]);
  
  const toggle = (value) => {
    setSelected(prev => 
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };
  
  return (
    <div className="mt-3 space-y-2">
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => toggle(opt.value)}
          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors border ${
            selected.includes(opt.value)
              ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]'
              : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
          }`}
        >
          {opt.label}
        </button>
      ))}
      <button
        type="button"
        onClick={() => onSubmit(selected)}
        className="w-full py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600"
      >
        Valider ({selected.length} sélectionné{selected.length > 1 ? 's' : ''})
      </button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MANUAL MODE - Formulaire de configuration
// ═══════════════════════════════════════════════════════════════════════════════

function ManualMode({ knowledgeBase, onSave, saving }) {
  const [activeSection, setActiveSection] = useState('profile');
  
  if (!knowledgeBase) return null;
  
  const sections = [
    { id: 'profile', label: 'Mon Profil', icon: User },
    { id: 'style', label: 'Mon Style', icon: Pencil },
    { id: 'rules', label: 'Mes Règles', icon: Zap },
    { id: 'templates', label: 'Mes Modèles', icon: FileText },
    { id: 'documents', label: 'Mes Documents', icon: BookOpen },
  ];
  
  return (
    <div className="grid md:grid-cols-[200px,1fr] gap-6">
      {/* Sidebar */}
      <div className="space-y-1">
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              activeSection === section.id
                ? 'bg-[#1E3A5F] text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <section.icon className="w-4 h-4" />
            {section.label}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <AnimatePresence mode="wait">
          {activeSection === 'profile' && (
            <ProfileSection knowledgeBase={knowledgeBase} onSave={onSave} saving={saving} />
          )}
          {activeSection === 'style' && (
            <StyleSection knowledgeBase={knowledgeBase} onSave={onSave} saving={saving} />
          )}
          {activeSection === 'rules' && (
            <RulesSection knowledgeBase={knowledgeBase} onSave={onSave} saving={saving} />
          )}
          {activeSection === 'templates' && (
            <TemplatesSection knowledgeBase={knowledgeBase} userId={knowledgeBase.user_id} />
          )}
          {activeSection === 'documents' && (
            <DocumentsSection knowledgeBase={knowledgeBase} userId={knowledgeBase.user_id} />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

// Section Profile
function ProfileSection({ knowledgeBase, onSave, saving }) {
  const [form, setForm] = useState({
    display_name: knowledgeBase.display_name || '',
    company_name: knowledgeBase.company_name || '',
    job_title: knowledgeBase.job_title || '',
    phone: knowledgeBase.phone || '',
    email: knowledgeBase.email || '',
  });
  
  const handleSave = () => onSave(form);
  
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Mon Profil</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Prénom / Surnom</label>
          <input
            type="text"
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            placeholder="Comment veux-tu qu'on t'appelle ?"
          />
        </div>
        {knowledgeBase.user_type === 'pro' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'entreprise</label>
              <input
                type="text"
                value={form.company_name}
                onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Métier / Titre</label>
              <input
                type="text"
                value={form.job_title}
                onChange={(e) => setForm({ ...form, job_title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
              />
            </div>
          </>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            />
          </div>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#1E3A5F] hover:bg-[#2d5a8a]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Sauvegarder
        </Button>
      </div>
    </motion.div>
  );
}

// Section Style
function StyleSection({ knowledgeBase, onSave, saving }) {
  const style = knowledgeBase.communication_style || {};
  const [form, setForm] = useState({
    default_tone: style.default_tone || 'adaptive',
    greeting_formal: style.greeting_formal || 'Bonjour,',
    greeting_casual: style.greeting_casual || 'Salut !',
    closing_formal: style.closing_formal || 'Cordialement,',
    closing_casual: style.closing_casual || 'À bientôt !',
    signature: style.signature || '',
    forbidden_phrases: style.forbidden_phrases || [],
    preferred_phrases: style.preferred_phrases || [],
  });
  
  const handleSave = () => onSave({ communication_style: form });
  
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Mon Style de Communication</h3>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Ton par défaut</label>
          <div className="flex gap-2">
            {[
              { value: 'formal', label: 'Formel' },
              { value: 'casual', label: 'Décontracté' },
              { value: 'adaptive', label: 'Adaptatif' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setForm({ ...form, default_tone: opt.value })}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  form.default_tone === opt.value
                    ? 'bg-[#1E3A5F] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Salutation formelle</label>
            <input
              type="text"
              value={form.greeting_formal}
              onChange={(e) => setForm({ ...form, greeting_formal: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Salutation décontractée</label>
            <input
              type="text"
              value={form.greeting_casual}
              onChange={(e) => setForm({ ...form, greeting_casual: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Formule de fin formelle</label>
            <input
              type="text"
              value={form.closing_formal}
              onChange={(e) => setForm({ ...form, closing_formal: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Formule de fin décontractée</label>
            <input
              type="text"
              value={form.closing_casual}
              onChange={(e) => setForm({ ...form, closing_casual: e.target.value })}
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            />
          </div>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Signature</label>
          <textarea
            value={form.signature}
            onChange={(e) => setForm({ ...form, signature: e.target.value })}
            rows={3}
            className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            placeholder="Jean Dupont&#10;06 12 34 56 78&#10;www.monsite.fr"
          />
        </div>
        
        <Button onClick={handleSave} disabled={saving} className="bg-[#1E3A5F] hover:bg-[#2d5a8a]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Sauvegarder
        </Button>
      </div>
    </motion.div>
  );
}

// Section Rules
function RulesSection({ knowledgeBase, onSave, saving }) {
  const rules = knowledgeBase.business_rules || {};
  const [form, setForm] = useState({
    always_do: rules.always_do || [],
    never_do: rules.never_do || [],
    service_area: rules.service_area || '',
    response_delay: rules.response_delay || 'sous 48h',
    pricing_info: rules.pricing_info || '',
  });
  const [newAlways, setNewAlways] = useState('');
  const [newNever, setNewNever] = useState('');
  
  const addRule = (type) => {
    const value = type === 'always' ? newAlways : newNever;
    const field = type === 'always' ? 'always_do' : 'never_do';
    if (value.trim()) {
      setForm({ ...form, [field]: [...form[field], value.trim()] });
      type === 'always' ? setNewAlways('') : setNewNever('');
    }
  };
  
  const removeRule = (type, index) => {
    const field = type === 'always' ? 'always_do' : 'never_do';
    setForm({ ...form, [field]: form[field].filter((_, i) => i !== index) });
  };
  
  const handleSave = () => onSave({ business_rules: form });
  
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Mes Règles</h3>
      <div className="space-y-6">
        {/* TOUJOURS */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Check className="w-4 h-4 inline mr-1 text-emerald-500" />
            TOUJOURS faire
          </label>
          <div className="space-y-2 mb-2">
            {form.always_do.map((rule, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg">
                <span className="flex-1 text-sm text-emerald-800">{rule}</span>
                <button onClick={() => removeRule('always', idx)} className="text-emerald-600 hover:text-red-500">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newAlways}
              onChange={(e) => setNewAlways(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addRule('always')}
              placeholder="Ex: Demander l'adresse avant tout devis"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
            <Button type="button" onClick={() => addRule('always')} variant="outline" size="sm">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* JAMAIS */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Ban className="w-4 h-4 inline mr-1 text-red-500" />
            NE JAMAIS faire
          </label>
          <div className="space-y-2 mb-2">
            {form.never_do.map((rule, idx) => (
              <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-lg">
                <span className="flex-1 text-sm text-red-800">{rule}</span>
                <button onClick={() => removeRule('never', idx)} className="text-red-600 hover:text-red-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newNever}
              onChange={(e) => setNewNever(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addRule('never')}
              placeholder="Ex: Donner un prix sans voir le chantier"
              className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg"
            />
            <Button type="button" onClick={() => addRule('never')} variant="outline" size="sm">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {knowledgeBase.user_type === 'pro' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Zone d'intervention</label>
                <input
                  type="text"
                  value={form.service_area}
                  onChange={(e) => setForm({ ...form, service_area: e.target.value })}
                  placeholder="Ex: Lyon et 30km"
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Délai de réponse</label>
                <select
                  value={form.response_delay}
                  onChange={(e) => setForm({ ...form, response_delay: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="dans l'heure">Dans l'heure</option>
                  <option value="dans la journée">Dans la journée</option>
                  <option value="sous 24h">Sous 24h</option>
                  <option value="sous 48h">Sous 48h</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Infos tarifs</label>
              <textarea
                value={form.pricing_info}
                onChange={(e) => setForm({ ...form, pricing_info: e.target.value })}
                rows={3}
                placeholder="Ex: Déplacement 50€, Taux horaire 45€/h..."
                className="w-full px-4 py-2 border border-gray-200 rounded-lg"
              />
            </div>
          </>
        )}
        
        <Button onClick={handleSave} disabled={saving} className="bg-[#1E3A5F] hover:bg-[#2d5a8a]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Sauvegarder
        </Button>
      </div>
    </motion.div>
  );
}

// Section Templates (placeholder)
function TemplatesSection({ knowledgeBase, userId }) {
  const [templates, setTemplates] = useState(knowledgeBase.templates || []);
  const [showAdd, setShowAdd] = useState(false);
  
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Mes Modèles de Réponses</h3>
        <Button onClick={() => setShowAdd(true)} size="sm" className="bg-[#1E3A5F]">
          <Plus className="w-4 h-4 mr-1" /> Ajouter
        </Button>
      </div>
      
      {templates.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>Aucun modèle pour l'instant</p>
          <p className="text-sm">Crée des modèles pour répondre plus vite !</p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map(tpl => (
            <div key={tpl.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-gray-900">{tpl.name}</span>
                <span className="text-xs px-2 py-1 bg-gray-200 rounded-full">{tpl.category}</span>
              </div>
              <p className="text-sm text-gray-600 line-clamp-2">{tpl.content}</p>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

// Section Documents (placeholder)
function DocumentsSection({ knowledgeBase, userId }) {
  const [documents, setDocuments] = useState(knowledgeBase.documents || []);
  
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Mes Documents / FAQ</h3>
        <Button size="sm" className="bg-[#1E3A5F]">
          <Plus className="w-4 h-4 mr-1" /> Ajouter
        </Button>
      </div>
      
      {documents.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>Aucun document pour l'instant</p>
          <p className="text-sm">Ajoute tes FAQ, tarifs, CGV...</p>
        </div>
      ) : (
        <div className="space-y-3">
          {documents.map(doc => (
            <div key={doc.id} className="p-4 bg-gray-50 rounded-xl border border-gray-200">
              <span className="font-medium text-gray-900">{doc.title}</span>
              <span className="ml-2 text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">{doc.doc_type}</span>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
